import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dice5, Trash2, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import { LogBet } from "@/components/LogBet";
import { TicketChat } from "@/components/TicketChat";
import { api, errMsg } from "@/lib/api";
import { fmtOdds, pct, money, SPORT_META } from "@/lib/odds";
import { useDataSource } from "@/lib/dataSource";

const STATUS = {
  pending: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  won: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  lost: "bg-red-500/15 text-red-400 border-red-500/40",
  push: "bg-amber-500/15 text-amber-300 border-amber-500/40",
};
const FILTERS = ["all", "pending", "won", "lost"];

export default function MyParlays() {
  const live = useDataSource().mode === "live";
  const [parlays, setParlays] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/parlays").then((r) => setParlays(r.data)).catch(() => setParlays([]));
  useEffect(() => { load(); }, []);

  const settle = async (id) => {
    try {
      const { data } = await api.post(`/parlays/${id}/settle`);
      setParlays((p) => p.map((x) => (x.parlay_id === id ? data : x)));
      toast[data.status === "won" ? "success" : "error"](data.status === "won" ? `Cashed! +${money(data.profit)}` : "Parlay lost. On to the next slate.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const settleAll = async () => {
    try { await api.post("/parlays/settle-all"); await load(); toast.success("All pending parlays settled"); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/parlays/${id}`); setParlays((p) => p.filter((x) => x.parlay_id !== id)); } catch (e) { toast.error(errMsg(e)); }
  };

  const share = async (id) => {
    const url = `${window.location.origin}/t/${id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch {}
    window.open(url, "_blank", "noopener");
  };

  const list = (parlays || []).filter((p) => filter === "all" || p.status === filter);
  const pending = (parlays || []).filter((p) => p.status === "pending").length;

  return (
    <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="my-parlays-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps text-emerald-400">Your tickets</p>
          <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">My Parlays</h1>
        </div>
        <button data-testid="settle-all-btn" onClick={settleAll} disabled={!pending} className="btn-ghost"><Dice5 size={16} /> {live ? "Check final scores" : "Simulate all results"} ({pending})</button>
      </div>
      <div className="mt-6 flex gap-2" data-testid="saved-parlays-tab">
        {FILTERS.map((f) => <button key={f} data-testid={`parlay-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${filter === f ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}>{f}</button>)}
      </div>

      <div className="mt-6 space-y-4">
        {parlays && list.length === 0 && (
          <div className="glass-card p-10 text-center text-sm text-slate-500" data-testid="parlays-empty">No parlays here yet. <Link to="/dashboard" className="text-emerald-400 underline">Build one from today's picks.</Link></div>
        )}
        {list.map((p, i) => (
          <article key={p.parlay_id} data-testid="saved-parlay-card" className={`glass-card fade-up p-5 ${p.status === "won" ? "!border-emerald-500/40" : ""}`} style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-2xl font-bold tracking-tight text-white" data-testid="parlay-title">{p.title}</h3>
                  {p.ai_optimized && <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-cyan-400"><Sparkles size={10} /> AI</span>}
                </div>
                <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleString()} · {p.legs.length} legs</p>
              </div>
              <span data-testid="parlay-status-badge" className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${STATUS[p.status]}`}>{p.status}</span>
            </div>
            <ul className="mt-4 divide-y divide-[#26334D]/60 rounded-lg border border-[#26334D] bg-[#0B0E14]/60">
              {p.legs.map((l) => (
                <li key={l.pick_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="h-6 w-1 rounded-full" style={{ background: SPORT_META[l.sport].color }} />
                  <span className="min-w-0 flex-1 truncate"><span className="text-slate-500">{l.matchup} · </span><span className="font-semibold text-white">{l.selection}</span></span>
                  {l.actual != null && l.result !== "pending" && (
                    <span data-testid="leg-actual" title={`Final box score: ${l.actual} ${l.stat || ""}`} className={`shrink-0 rounded-md border px-2 py-0.5 font-mono-num text-[11px] font-bold ${l.result === "won" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : l.result === "lost" ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                      {l.actual} <span className="font-medium text-slate-400">actual</span>{/(Over|Under) ([\d.]+)/.test(l.selection) && <span className="text-slate-500"> · line {l.selection.match(/(Over|Under) ([\d.]+)/)[2]}</span>}
                    </span>
                  )}
                  <span className="font-mono-num text-amber-400">{fmtOdds(l.odds)}</span>
                  <span className={`w-12 text-right text-xs font-bold uppercase ${l.result === "won" ? "text-emerald-400" : l.result === "lost" ? "text-red-400" : "text-slate-500"}`}>{l.result === "pending" ? "—" : l.result}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
              <Meta label="Wager" value={money(p.wager)} />
              <Meta label="Odds" value={fmtOdds(p.combined_american)} color="text-amber-400" />
              <Meta label="Win prob" value={pct(p.win_probability)} color="text-emerald-400" />
              <Meta label="Payout" value={money(p.potential_payout)} />
              {p.profit != null && <Meta label="P/L" value={`${p.profit >= 0 ? "+" : ""}${money(p.profit)}`} color={p.profit >= 0 ? "text-emerald-400" : "text-red-400"} />}
              <LogBet parlay={p} onChange={(np) => setParlays((l) => l.map((x) => (x.parlay_id === np.parlay_id ? { ...x, ...np, placed: np.placed, real_profit: np.real_profit } : x)))} />
              <div className="ml-auto flex gap-2">
                <button data-testid="parlay-share-btn" onClick={() => share(p.parlay_id)} className="btn-ghost !py-1.5 !px-3 text-xs"><Share2 size={14} /> Share</button>
                {p.status === "pending" && <button data-testid="parlay-settle-btn" onClick={() => settle(p.parlay_id)} className="btn-ghost !py-1.5 !px-3 text-xs"><Dice5 size={14} /> {live ? "Check result" : "Simulate result"}</button>}
                <button data-testid="parlay-delete-btn" onClick={() => remove(p.parlay_id)} className="rounded-full p-2 text-slate-500 transition-colors hover:bg-red-500/15 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="mt-3"><TicketChat parlayId={p.parlay_id} /></div>
          </article>
        ))}
      </div>
    </main>
  );
}

function Meta({ label, value, color = "text-white" }) {
  return <div><p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p><p className={`font-mono-num font-bold ${color}`}>{value}</p></div>;
}
